import { ListJobsService } from "@application/useCases";
import { StatusEnumSchema } from "@domain/schemas/BI";
import { LogDeliveryMode, LogMessage, subscribeToBatchLogs, subscribeToLogMessages } from "@infrastructure/realtime/logsHubClient";
import LogsDialog from "@presentation/components/job/components/LogsDialog";
import React, { useEffect, useRef, useState } from "react";

type BatchArtifactsTarget = {
  jobId: string;
  id: string;
  jobPipelineStepId: string;
  status?: StatusEnumSchema | null;
};

type ArtifactMode = "logs" | "results";

const getOrCreateSessionId = () => {
  const key = "my-app-session-id";
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
};

const normalizeDeliveryMode = (deliveryMode?: LogDeliveryMode) => {
  switch (deliveryMode) {
    case "Append":
    case 1: {
      return "Append";
    }
    case "Prepend":
    case 2: {
      return "Prepend";
    }
    default: {
      return "Replace";
    }
  }
};

const applyLogMessage = (previous: string, logs: LogMessage) => {
  switch (normalizeDeliveryMode(logs.deliveryMode)) {
    case "Prepend": {
      return previous ? `${logs.content}\n${previous}` : logs.content;
    }
    case "Append": {
      return previous ? `${previous}\n${logs.content}` : logs.content;
    }
    default: {
      return logs.content;
    }
  }
};

export const useBatchArtifactsViewer = (listJobTasksService: ListJobsService) => {
  const [selectedBatch, setSelectedBatch] = useState<BatchArtifactsTarget | null>(null);
  const [isLogsDialogVisible, setIsLogsDialogVisible] = useState(false);
  const [isResultsVisible, setIsResultsVisible] = useState(false);
  const [logContent, setLogContent] = useState("");
  const selectedBatchReference = useRef<BatchArtifactsTarget | null>(null);
  const selectedSessionIdReference = useRef<string | null>(null);
  const selectedModeReference = useRef<ArtifactMode | null>(null);
  const unsubscribeBatchLogsReference = useRef<(() => void) | null>(null);

  const stopActiveStream = async () => {
    unsubscribeBatchLogsReference.current?.();
    unsubscribeBatchLogsReference.current = null;

    if (selectedBatchReference.current && selectedSessionIdReference.current && selectedModeReference.current === "logs") {
      try {
        await listJobTasksService.stopLogsAudit(
          selectedBatchReference.current.jobId,
          selectedBatchReference.current.jobPipelineStepId,
          selectedBatchReference.current.id,
          selectedSessionIdReference.current,
        );
      } catch (error) {
        console.error(error);
      }
    }

    selectedBatchReference.current = null;
    selectedSessionIdReference.current = null;
    selectedModeReference.current = null;
  };

  const startStream = (target: BatchArtifactsTarget, mode: ArtifactMode, sessionId: string) => {
    unsubscribeBatchLogsReference.current = subscribeToBatchLogs({
      jobId: target.jobId,
      jobPipelineStepId: target.jobPipelineStepId,
      batchId: target.id,
      sessionId,
    });

    selectedBatchReference.current = target;
    selectedSessionIdReference.current = sessionId;
    selectedModeReference.current = mode;
  };

  const openLogs = async (jobId: string, jobPipelineStepId: string, batch: Omit<BatchArtifactsTarget, "jobId" | "jobPipelineStepId">) => {
    await stopActiveStream();

    const target: BatchArtifactsTarget = { ...batch, jobId, jobPipelineStepId };
    const sessionId = getOrCreateSessionId();
    setLogContent("");
    setSelectedBatch(target);
    setIsResultsVisible(false);
    setIsLogsDialogVisible(true);

    startStream(target, "logs", sessionId);
    await listJobTasksService.startLogsAudit(target.jobId, target.jobPipelineStepId, target.id, sessionId);
  };

  const openResults = async (jobId: string, jobPipelineStepId: string, batch: Omit<BatchArtifactsTarget, "jobId" | "jobPipelineStepId">) => {
    await stopActiveStream();

    const target: BatchArtifactsTarget = { ...batch, jobId, jobPipelineStepId };
    const sessionId = getOrCreateSessionId();
    setLogContent("");
    setSelectedBatch(target);
    setIsLogsDialogVisible(false);
    setIsResultsVisible(true);

    startStream(target, "results", sessionId);
    await listJobTasksService.getResults(target.jobId, target.jobPipelineStepId, target.id, sessionId);
  };

  const closeDialog = async () => {
    setIsLogsDialogVisible(false);
    setIsResultsVisible(false);

    await stopActiveStream();
    setLogContent("");
    setSelectedBatch(null);
  };

  useEffect(() => {
    getOrCreateSessionId();

    const unsubscribeFromLogMessages = subscribeToLogMessages((logs) => {
      if (!selectedBatchReference.current || !selectedSessionIdReference.current || logs.batchId !== selectedBatchReference.current.id || logs.sessionId !== selectedSessionIdReference.current) {
        return;
      }

      setLogContent((previous) => applyLogMessage(previous, logs));
    });

    return () => {
      unsubscribeFromLogMessages();
      void stopActiveStream();
    };
  }, []);

  let artifactsDialog: React.ReactNode = null;
  if (isResultsVisible && selectedBatch) {
    artifactsDialog = <LogsDialog logContent={logContent} onClose={closeDialog} isResult={true} isRunning={false} />;
  } else if (isLogsDialogVisible && selectedBatch) {
    artifactsDialog = <LogsDialog logContent={logContent} onClose={closeDialog} isResult={false} isRunning={selectedBatch.status === StatusEnumSchema.Running} focusLastLineOnOpen={true} />;
  }

  return {
    artifactsDialog,
    openLogs,
    openResults,
  };
};
